import { Component, type ReactNode, type ErrorInfo } from "react";
import { FormattedMessage } from "react-intl";

import { isGraphQLError } from "util/graphql/query";
import ErrorImage from "assets/images/signer/computer-error.svg";
import Button from "common/core/button";
import { segmentTrack } from "util/segment";
import { captureException } from "util/exception";
import { isHardNavigating } from "util/navigation";
import { isOfflineError, isChunkLoadError } from "util/http";
import { ButtonStyledLink } from "common/core/button/button_styled_link";

import Styles from "./index.module.scss";

type Props = { children: ReactNode };

const initialState = Object.freeze({ currentError: null as "ignore" | null | Error });

function isIgnoreableError(error: Error): boolean {
  // Webpack loads dynamic import() calls deep inside its runtime. If these fail, a ChunkLoadError is raised.
  // A common use of import() is with React.lazy, meaning this React ErrorBoundary will sometimes catch import()
  // exceptions. However, if we are "hard navigating," browsers will cancel any outstanding script downloads and network requests
  // and since location.assign allows the current page to continue operation until the next url is loaded, the user
  // might see a flash of "something went wrong" before navigation completes.
  // The same can happen for outstanding network requests. Therefore, we ignore these errors
  // since they are expected and the full page load coming up will be the user's "retry," if needed.

  if (!isHardNavigating()) {
    return false;
  }
  return isOfflineError(error) || isChunkLoadError(error);
}

class ErrorBoundary extends Component<Props, typeof initialState> {
  static getDerivedStateFromError(error: Error) {
    return { currentError: isIgnoreableError(error) ? "ignore" : error };
  }

  state = initialState;

  handleRetryClick = () => {
    const currentError = this.state.currentError as Error;
    // eslint-disable-next-line no-console
    console.warn(
      `User attempted React re-render after root <ErrorBoundary /> catch - ${currentError.message}`,
    );
    window.location.reload();
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isIgnoreableError(error)) {
      // eslint-disable-next-line no-console
      return console.log(`<ErrorBoundary /> caught ignorable error ${error.name} ${error.message}`);
    }
    // Would be good to keep the order of Sentry and then segment, just in case error is not an object
    captureException(error, info as unknown as Record<string, unknown>);
    segmentTrack("React encountered fatal rendering error", {
      error: { message: error.message, stack: error.stack },
      info,
    });
    // console the full error minus the stack so we get the focused detail in sumo
    if (isGraphQLError(error)) {
      // eslint-disable-next-line no-console
      console.error(
        "React encountered fatal rendering error [graphql-error]",
        error.name,
        error.message,
        error.cause,
        error.extraInfo,
        error.clientErrors,
        error.graphQLErrors,
        error.protocolErrors,
        error.networkError,
      );
    } else {
      // eslint-disable-next-line no-console
      console.error("React encountered fatal rendering error [error]", error);
    }
  }

  renderErrorState() {
    return (
      <div className={Styles.background}>
        <div className={Styles.error}>
          <img src={ErrorImage} alt="" aria-hidden="true" />
          <FormattedMessage
            id="b7b3f86f-c8e6-4a0b-b7f6-5d1a6ef9bf4f"
            tagName="h1"
            description="Title of the error page"
            defaultMessage="Something went wrong..."
          />
          <Button buttonColor="action" variant="primary" onClick={this.handleRetryClick}>
            <FormattedMessage
              id="f90e481a-e011-4189-a480-c999ab65ad44"
              description="Retry/refresh button on the error page"
              defaultMessage="Retry"
            />
          </Button>
          <ButtonStyledLink
            href="/"
            openInCurrentTab
            automationId="return-to-dashboard-button"
            buttonColor="action"
            variant="primary"
          >
            <FormattedMessage
              id="90498ee6-683d-4300-a61a-df516da78771"
              defaultMessage="Return to dashboard"
            />
          </ButtonStyledLink>
        </div>
      </div>
    );
  }

  render() {
    const { currentError } = this.state;
    return currentError === "ignore"
      ? null
      : currentError
        ? this.renderErrorState()
        : this.props.children;
  }
}

export default ErrorBoundary;
